import React, { useState, useEffect, useCallback } from 'react';
import API_STATES from '@/constants/StateConstants';
import EmptyTable from '@/components/General/EmptyTable';
import DataTable from '@/components/General/DataTable';
import styles from '../HomepageNotification.module.scss';
import * as MessageService from '@/services/api/subscriptionBasedNotification.service';
import generalStyles from '@/components/General/Styles.module.scss';
import deleteIcon from '@images/icons/deleteIcon.svg';
import editIcon from '@images/icons/EditIcon.svg';
import Popup from 'reactjs-popup';
import 'reactjs-popup/dist/index.css';
import moment from 'moment';
import ByzzerCSRModal from '@/components/ByzzerCSRModal';
import SubscriptionBasedNotificationForm from './SubscriptionBasedNotificationForm';
import * as Constant from '../Constants';
import { customizeDate } from '../Utils';

function SubscriptionBasedNotificationDetailedHistory({ refreshHistory, refresh, selectedNotification }) {
    const [notificationApiStatus, setNotificationApiStatus] = useState(API_STATES.none);
    const [notificationApiError, setNotificationApiError] = useState('');
    const [notificationList, setNotificationList] = useState([]);
    const [notificationDeleteStatus, setNotificationDeleteStatus] = useState(API_STATES.none);
    const [notificationDeleteError, setNotificationDeleteError] = useState('');
    const [openModal, setOpenModal] = useState(false);
    const [selectedRowData, setSelectedRowData] = useState({});
    const [edit, setEdit] = useState(false);
    
    const COLUMNS = [
        {
            Header: 'Notification ID',
            accessor: 'notificationId',
            className: generalStyles.tableCell,
        },
        {
            Header: 'Companies',
            accessor: 'company',
            className: generalStyles.tableCell,
        },
        {
            Header: 'User',
            accessor: 'email',
            className: generalStyles.tableCell,
        },
        {
            Header: 'Expiration date',
            accessor: 'endDate',
            className: generalStyles.tableCell,
        },
        {
            Header: 'Type of Notification',
            accessor: 'notificationType',
            className: generalStyles.tableCell,
        },
        {
            Header: 'User Action',
            accessor: 'userAction',
            className: generalStyles.tableCell,
        },
        {
            Header: 'Date of User Action',
            accessor: 'userActionDate',
            className: generalStyles.tableCell,
        },
        {
            Header: 'Action',
            accessor: (row) => row,
            id: 'actions',
            disableFilters: true,
            disableSortBy: true,
            className: generalStyles.tableCell,
            Cell: ({ value: row }) => {
                const isExpired = moment(row.expiration).isAfter(moment(new Date()));
                return (
                    <div className={styles.actionContainer}>
                        <div
                            className={styles.expandIconContainer}
                            onClick={
                                isExpired
                                    ? () => {
                                          deleteClicked(row);
                                      }
                                    : {}
                            }
                        >
                            <img
                                src={deleteIcon}
                                alt="delete"
                                className={isExpired ? styles.btnWidth : styles.disabledIcon}
                            />
                        </div>
                        <div
                            className={styles.expandIconContainer}
                            onClick={() => {
                                editClicked(row);
                            }}
                        >
                            <img src={editIcon} alt="edit" className={styles.btnWidth} />
                        </div>
                    </div>
                );
            },
        }
    ];

    const getNotificationHistory = useCallback(async () => {
        if (selectedNotification) {
            refresh(false);
            setNotificationApiStatus(API_STATES.loading);
            let response = await MessageService.getNotificationDetail(
                Constant.MESSAGE_TYPE.SUBSCRIPTION_BASED,
                selectedNotification.notificationId
            );
            if (response.status === API_STATES.success) {
                const result = response.data;
                setNotificationApiStatus(API_STATES.success);
                setNotificationList(result.data);
            } else {
                setNotificationApiError(response.error);
                setNotificationList([]);
                setNotificationApiStatus(API_STATES.error);
            }
        } else return false;
    }, [refresh, selectedNotification]);

    useEffect(() => {
        if (refreshHistory) {
            getNotificationHistory();
        }
    }, [refreshHistory, getNotificationHistory]);

    const transformTabelColumns = (list) => {
        const historyData = [];
        let type = {};

        let userAction;
        let userActionDate;

        for (const data of list) {
            type = Constant.NOTIFICATION_TYPE.filter((item) => {
                return item.value === data.messageType;
            });
            userAction = '';
            userActionDate = '';
            // checking the latest action performed by the user
            if (data.isDone === true) {
                userAction = Constant.USER_ACTIONS.ALREADY_DONE;
                userActionDate = customizeDate(data.readDtm);
            }else if (data.isDeleted === true && data.deletedDtm !== null) {
                userAction = Constant.USER_ACTIONS.DISMISS;
                userActionDate = customizeDate(data.deletedDtm);
            }else if (data.readDtm !== null && data.snoozeDtm === null) {
                userAction = Constant.USER_ACTIONS.DONE;
                userActionDate = customizeDate(data.readDtm);
            } else if (data.readDtm === null && data.snoozeDtm !== null) {
                userAction = Constant.USER_ACTIONS.DO_LATER;
                userActionDate = customizeDate(data.snoozeDtm);
            } else if (data.readDtm !== null && data.snoozeDtm !== null) {
                if (moment(new Date(data.readDtm)).isAfter(moment(new Date(data.snoozeDtm)))) {
                    userAction = Constant.USER_ACTIONS.DONE;
                    userActionDate = customizeDate(data.readDtm);
                } else {
                    userAction = Constant.USER_ACTIONS.DO_LATER;
                    userActionDate = customizeDate(data.snoozeDtm);
                }
            }
            let dataObj = {
                
                notificationId: data.notificationId,
                endDate: customizeDate(data.endDate),
                notificationType: data.messageType,
                type: type,
                expiration: data.endDate,
                csrNotificationId: data.csrConfigId,
                subscriptionNotificationType: data.criteria?.notificationType,
                recepientId: data.recepientId,
                companyId: data.company_id,
                company: data.company,
                user: data.firstName + ' ' + data.lastName,
                email: data.email,
                userAction: userAction,
                userActionDate: userActionDate,

            };
            historyData.push(dataObj);
        }
        return historyData;
    };

    function deleteClicked(rowData) {
        setSelectedRowData(rowData);
        setOpenModal(true);
    }

    function editClicked(rowData) {
        const newRowData = {
            ...rowData,
            content: selectedNotification?.content,
            message: selectedNotification?.message,
            header: selectedNotification?.header,
            hyperLink: selectedNotification?.hyperLink,
            runType: selectedNotification?.runType,
            subscriptionList: selectedNotification?.subscriptionList,
            subscriptionTypes: selectedNotification?.subscriptionTypes
        }
        setSelectedRowData(newRowData);
        setEdit(true);
    }

    function closeEdit() {
        setEdit(false);
        setSelectedRowData(null);
    }

    function getTerminateReqBody() {
        return {
            id: selectedRowData.id,
            recepientId: selectedRowData.recepientId,
            notificationId: selectedRowData.notificationId,
            companyId: selectedRowData.companyId,
            expirationDtm: moment(new Date()).startOf('day').format('YYYY/MM/DD HH:mm:ss'),
            subscriptionMessage : {
                subscriptionTypes: selectedNotification?.subscriptionList,
                subscriptionTriggerType: "subscription",
                startDtm: null,
                endDtm: null
            },
            notification: { message: selectedNotification.message, header: selectedNotification.header, hyperLink: selectedNotification.hyperLink },
            messageType: selectedRowData.notificationType,
            triggerType: Constant.CUSTOM,
            priority: Constant.PRIORITY.LOW,
            messageCreationType: Constant.MESSAGE_TYPE.SUBSCRIPTION_BASED,
        };
    }

    async function terminateNotification() {
        try {
            setOpenModal(false);
            if (selectedRowData) {
                setNotificationDeleteStatus(API_STATES.loading);
                const requestBody = getTerminateReqBody();
                let response = await MessageService.editSingleUserMessage(requestBody);
                if (response.status === API_STATES.success) {
                    setNotificationDeleteStatus(API_STATES.success);
                    refresh(true);
                } else {
                    setNotificationDeleteError(response.error);
                    setNotificationDeleteStatus(API_STATES.error);
                    return false;
                }
            } else {
                return false;
            }
        } catch (err) {
            setNotificationDeleteError(err);
            setNotificationDeleteStatus(API_STATES.error);
        }
    }

    return (
        <>
            <h3>Detailed Notification History</h3>
            <br />
            <div>
                {notificationDeleteStatus === API_STATES.error && (
                    <p className={generalStyles.errorMessage}>Error: {notificationDeleteError}</p>
                )}
                {notificationDeleteStatus === API_STATES.loading && (
                    <p className={generalStyles.errorMessage}>Terminating...</p>
                )}
                {notificationApiStatus === API_STATES.error && (
                    <p className={generalStyles.errorMessage}>Error: {notificationApiError}</p>
                )}
                {notificationApiStatus === API_STATES.none && <p className={generalStyles.statusMessage}>Loading...</p>}
                {notificationApiStatus === API_STATES.loading && (
                    <p className={generalStyles.statusMessage}>Loading...</p>
                )}
                {notificationApiStatus === API_STATES.success && (
                    <>
                        {notificationList.length > 0 ? (
                            <DataTable data={transformTabelColumns(notificationList)} columns={COLUMNS} withDownload downloadFileName={"SubscriptionBasedNotificationsDetailedHistory"}/>
                        ) : (
                            <div>
                                <EmptyTable
                                    noValueDescription="No Notification History found."
                                    columns={[
                                        'Notification ID',
                                        'Flagged Subscription Types',
                                        'Companies',
                                        'User',
                                        'Expiration date',
                                        'Type of Notification',
                                        'Notification Content',
                                        'Link',
                                        'User Action',
                                        'Date of User Action',
                                        'Action',
                                    ]}
                                />
                            </div>
                        )}
                    </>
                )}
            </div>

            {openModal && (
                <Popup
                    open={openModal}
                    onClose={() => {
                        setOpenModal(false);
                    }}
                    modal
                >
                    {() => (
                        <div className={styles.warningDialog}>
                            <h2>Warning!</h2>
                            <div>
                                <div>
                                    {' '}
                                    <b>Are you sure you want to terminate the Notification for this company?</b>
                                </div>
                                <br/>
                                <button
                                    className={`${generalStyles.confirmOperationButton} ${styles.btnWidth}`}
                                    onClick={() => {
                                        terminateNotification();
                                    }}
                                >
                                    Terminate for single user
                                </button>
                                <button
                                    className={`${generalStyles.cancelOperationButton} ${styles.btnWidth}`}
                                    onClick={() => {
                                        setOpenModal(false);
                                    }}
                                >
                                    Cancel
                                </button>{' '}
                            </div>
                        </div>
                    )}
                </Popup>
            )}

            {edit && (
                <ByzzerCSRModal
                    show={true}
                    onClose={() => setEdit(false)}
                    size={'extra-large'}
                    type={'info'}
                    headerType={'none'}
                    children={
                        <SubscriptionBasedNotificationForm
                            selectedNotification={selectedRowData}
                            edit={edit}
                            refresh={(flag) => refresh(flag)}
                            closeEdit={() => closeEdit()}
                            editSingleNotification={edit}
                        />
                    }
                />
            )}
        </>
    );
}
export default SubscriptionBasedNotificationDetailedHistory;
